/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { ACTION_WIDGET_SHOW_MORE, CATEGORY_WIDGET_GRID } from 'utils/analytics/eventTypesConstants';
import { Button } from 'omakase';
import { Fade } from '@material-ui/core';
import { Grid } from 'ui';
import { GridWidget as GridWidgetType, Modifiers } from '@tock/shared/types/widget';
import { PhotoCardSkeleton } from 'consumer-page-widgets/PhotoCard';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { WidgetCard } from 'consumer-page-widgets/WidgetCard';
import { WidgetContainer } from 'consumer-page-widgets/WidgetContainer';
import { WidgetListItemContent } from 'types';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { consumer } from '@tock/coretypes';
import { currentMetroAreaSelector } from 'selectors/metroAreaSelectors';
import { getDataLength, getTockContent, getTockMedium, mapContentToItems } from 'utils/widgetUtils';
import { gridItemStyle } from 'consumer-page-widgets';
import { useInView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';
import WidgetLayoutContainer from 'consumer-page-widgets/WidgetUtils/WidgetLayoutContainer';
import clsx from 'clsx';

const useStyles = makeStyles((theme: Theme) => {
  const gridItem = gridItemStyle(theme);
  return createStyles({
    loadMore: {
      margin: '0 auto',
      display: 'flex',
    },
    gridItem,
    // 2020-04-01 Kelly M. - no joke, we need to make this into a wrapper similar to typography but for grid
    // if this becomes a more formal pattern, like 2 spacing on mobile but 3 on tablet
    // this mimics changing the spacing property to 3 on `sm` breakpoint
    cardSpacing: {
      [theme.breakpoints.up('sm')]: {
        '&.MuiGrid-spacing-xs-2': {
          margin: theme.spacing(-1.5),
          width: `calc(100% + 24px)`,
        },
        '&.MuiGrid-spacing-xs-2 > .MuiGrid-item': {
          padding: theme.spacing(1.5),
        },
      },
    },
  });
});

interface Props {
  baseWidgetClassName?: string;
  className?: string;
  fetchMoreWidgetData: any;
  isTitle: boolean;
  lazyLoad?: boolean;
  pathname: string;
  slug: string;
  type: consumer.ConsumerPageType;
  widget: GridWidgetType;
  widgetListIndex?: number;
}

interface GridSettings {
  xs?: number;
  sm?: number;
  md?: number;
}

const getDensityProps = (density: string, itemsLength: number) => {
  const map: Dictionary<GridSettings> = {
    low: {
      xs: 12,
      sm: 6,
      md: 3,
    },
    hero: {
      xs: 12,
      sm: 6,
    },
    medium: {
      xs: 6,
      sm: 6,
      md: 3,
    },
  };

  return map[density] ? map[density] : map.low;
};

export const GridWidgetLegacy: React.FC<Props> = ({
  baseWidgetClassName,
  className: passedClassName,
  fetchMoreWidgetData,
  isTitle,
  lazyLoad = false,
  pathname,
  slug,
  type,
  widget,
  widgetListIndex,
}) => {
  const { ref, inView } = useInView({
    rootMargin: '200px 0px',
    initialInView: !lazyLoad,
    triggerOnce: true,
  });

  const classes = useStyles();
  const [isRequesting, setIsRequesting] = React.useState<boolean>(false);
  const [fetchError, setFetchError] = React.useState<boolean>(false);
  const currentMetro = useSelector(currentMetroAreaSelector);

  const handleShowMore = () => {
    if (!isRequesting) {
      const { content, widgetInfo } = widget;
      const { numResults } = content as consumer.WidgetContent;
      const { id } = widgetInfo as consumer.WidgetInfo;
      const itemLength = getDataLength(content as consumer.WidgetContent);
      if (itemLength < numResults) {
        setIsRequesting(true);
        setFetchError(false);
        window.analytics.sendEvent(
          CATEGORY_WIDGET_GRID,
          ACTION_WIDGET_SHOW_MORE,
          widgetInfo?.title
        );
        fetchMoreWidgetData(slug, type, id, itemLength, undefined, currentMetro?.id)
          .then(() => {
            setIsRequesting(false);
          })
          .catch(() => {
            setIsRequesting(false);
            setFetchError(true);
          });
      }
    }
  };

  const { content, widgetInfo } = widget;
  const { numResults } = content as consumer.WidgetContent;
  const { title, subtitle, detailsPage } = widgetInfo as consumer.WidgetInfo;
  const showMore = getDataLength(content as consumer.WidgetContent) < numResults && !fetchError;
  const className = clsx(baseWidgetClassName, passedClassName);
  const gridSpacing = 2;

  const widgetDataItems = mapContentToItems(content as consumer.WidgetContent, 'IMGIX');

  if (widgetDataItems.length < 1) {
    return null;
  }

  const widgetModifiers = (widgetInfo?.widgetModifier as consumer.WidgetModifier[]) ?? [];
  const density = widgetModifiers.find((modifier) => modifier.key === Modifiers.density);
  const titleType = widgetModifiers.find((modifier) => modifier.key === Modifiers.titleType);

  const titleTypeValue = titleType?.value;
  const densityValue = density ? density.value : 'low';
  const densityProps = getDensityProps(densityValue, widgetDataItems.length);

  const getTestIdForItemType = (
    type: Exclude<consumer.ContentType, 'NAMED_CONFIGURATION'>,
    index: number
  ) => {
    switch (type) {
      case 'BUSINESS': {
        return `result-business-${index}`;
      }
      case 'BUSINESS_GROUP': {
        return `result-business-group-${index}`;
      }
      case 'METRO_AREA': {
        return `result-metro-area-${index}`;
      }
      case 'EXPERIENCE': {
        return `result-experience-${index}`;
      }
      case 'SHOP_ITEM': {
        return `result-shop-item-${index}`;
      }
      case 'SHOP_BRAND': {
        return `result-shop-brand-${index}`;
      }
      case 'SHOP_WINE_REGION': {
        return `result-shop-wine-region-${index}`;
      }
      default:
        assertNever(type);
    }
  };

  const slides = widgetDataItems.map((item: WidgetListItemContent, index: number) => {
    return (
      <Grid
        component="li"
        key={item.key}
        data-businessid={item.key}
        data-testid={getTestIdForItemType(item.type, index)}
        item
        {...(densityProps as any)}
        className={item.type === 'EXPERIENCE' ? classes.gridItem : undefined}
      >
        {lazyLoad && !inView ? (
          // approximate cards height
          <div style={{ flex: 1, paddingTop: '93%' }} />
        ) : (
          <WidgetCard
            pathname={pathname}
            item={item}
            modifiers={widgetModifiers}
            index={index}
            widgetListIndex={widgetListIndex}
            widgetName={widgetInfo?.title || undefined}
          />
        )}
      </Grid>
    );
  });

  let loadMore;
  if (showMore && !isRequesting) {
    loadMore = (
      <Button
        variant="primary"
        size="large"
        className={classes.loadMore}
        onClick={handleShowMore}
        data-testid="load-more-button"
      >
        Load more
      </Button>
    );
  }
  if (showMore && isRequesting) {
    loadMore = (
      <Grid container spacing={gridSpacing}>
        <Grid item {...(densityProps as any)}>
          <Fade in>
            <PhotoCardSkeleton />
          </Fade>
        </Grid>
        <Grid item {...(densityProps as any)}>
          <Fade in>
            <PhotoCardSkeleton />
          </Fade>
        </Grid>
        <Grid item {...(densityProps as any)}>
          <Fade in>
            <PhotoCardSkeleton />
          </Fade>
        </Grid>
        <Grid item {...(densityProps as any)}>
          <Fade in>
            <PhotoCardSkeleton />
          </Fade>
        </Grid>
      </Grid>
    );
  }

  const href = `${detailsPage}?tock_source=tock&tock_medium=${getTockMedium(
    pathname
  )}&tock_content=${getTockContent('grid', title)}`;

  return (
    <WidgetLayoutContainer
      ref={ref}
      className={className}
      data-testid={`widget-${title.replace(/\W/g, '').toLowerCase()}`}
    >
      <WidgetContainer
        title={title}
        isTitle={isTitle}
        isSubSection={titleTypeValue ? titleTypeValue === 'subsection' : false}
        subtitle={subtitle}
        href={detailsPage ? href : undefined}
        loadMore={detailsPage ? undefined : loadMore}
        contains="Widget Grid"
      >
        <Grid className={classes.cardSpacing} component="ul" container spacing={gridSpacing}>
          {slides}
        </Grid>
      </WidgetContainer>
    </WidgetLayoutContainer>
  );
};
